import { useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar as DatePickerCalendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { addDays, isToday } from "../calendar-utils";

/**
 * Sticky per-pane heading for the day view's two-day split: ‹ date › nav plus a
 * date-picker popover on the label. The close button only renders on the pane
 * that can be dismissed (the secondary day).
 */
export function DayPaneHeader({ date, onDateChange, onClose }) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // The picker hands back a JS Date at local midnight; preserve the pane date's
  // time-of-day/zone and only move the calendar day (mirrors the toolbar picker).
  const handlePickDate = (jsDate) => {
    if (!jsDate) return;
    onDateChange?.(
      date.set({
        year: jsDate.getFullYear(),
        month: jsDate.getMonth() + 1,
        day: jsDate.getDate(),
      }),
    );
    setIsPickerOpen(false);
  };

  return (
    <div className="relative flex h-9 min-w-0 flex-1 items-center justify-center gap-0.5 bg-background px-1">
      <button
        type="button"
        onClick={() => onDateChange?.(addDays(date, -1))}
        className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <span className="sr-only">Previous day</span>
        <ChevronLeft className="size-4" />
      </button>
      <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            title="Pick a date"
            className={cn(
              "flex h-7 min-w-0 items-center rounded-md px-2 text-xs font-semibold transition-colors hover:bg-muted",
              isToday(date) ? "text-primary" : "text-foreground",
            )}
          >
            <span className="truncate">{date.toFormat("cccc, d LLL yyyy")}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="center" className="w-auto p-0">
          <DatePickerCalendar
            mode="single"
            selected={date.toJSDate()}
            defaultMonth={date.toJSDate()}
            onSelect={handlePickDate}
            autoFocus
          />
          <div className="border-t border-border p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-center font-medium"
              onClick={() => handlePickDate(new Date())}
            >
              Today
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <button
        type="button"
        onClick={() => onDateChange?.(addDays(date, 1))}
        className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <span className="sr-only">Next day</span>
        <ChevronRight className="size-4" />
      </button>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          title="Close the second day"
          className="absolute right-1 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <span className="sr-only">Close the second day</span>
          <X className="size-4" />
        </button>
      ) : null}
    </div>
  );
}
